import {Folder} from '../../Assets.types';
import assetsApi from '../../Assets.api';
import {AssetFolderEntityType} from '../../Assets.enums';
import {fileUploadApi} from '../../../fileUpload/fileUpload.api';
import waitForJob from '../../../jobs/utils/waitForJob';
import {JobResult} from '../../../jobs/jobs.types';
import findFolderInTree from './utils/findFolderInTree';
import updateFolderInTree from './utils/updateFolderInTree';
import {backgroundProcessService} from '../../../backgroundProcess/backgroundProcess.service';
import {ProcessType} from '../../../backgroundProcess/backgroundProcess.types';
import validateFileSizes from './utils/validateFileSizes';
import normalizeAndValidateAssetFileName from './utils/normalizeAndValidateAssetFileName';
import {emit} from 'common/src/modules/toasts/toasts.utils';

/**
 * Load root folder tree for an entity type
 * @param entityType - Type of entity
 * @param entityId - Optional entity ID (for entity-specific folders like CreativeTemplate)
 * @param depth - Optional depth of the tree to retrieve
 * @returns Promise<AssetFolderTree> - Root folder tree
 */
export async function loadAssetTreeUtil(
  entityType: AssetFolderEntityType,
  entityId?: number,
  depth?: number,
): Promise<Folder> {
  if (entityId !== undefined) {
    return assetsApi.getRootFolderOfEntityObject(entityType, entityId, depth);
  }

  return assetsApi.getRootFolder(entityType, depth);
}

/**
 * Load folder contents if not already loaded
 * @param assetTree - Current asset tree
 * @param folderId - ID of folder to load
 * @param entityType - Entity type for API call
 * @param depth - Optional depth (defaults to 0 for direct children only)
 * @returns Updated tree with folder contents, or null if folder already loaded or not found
 */
export async function loadFolderContentsUtil(
  assetTree: Folder | null,
  folderId: number,
  entityType: AssetFolderEntityType,
  depth = 0,
): Promise<Folder | null> {
  if (!assetTree) {
    throw new Error('Asset tree not loaded. Please load asset tree first.');
  }

  const folder = findFolderInTree(assetTree, folderId);

  if (!folder) {
    throw new Error(`Folder with ID ${folderId} not found in tree`);
  }

  // Check if folder contents have been attempted to load
  if (folder.isChildrenLoaded === true) {
    // Folder contents already in tree, return null to indicate no update needed
    return null;
  }

  // Fetch folder contents
  const folderTree = await assetsApi.GetFolder(entityType, folderId, depth);

  // Update tree immutably with fetched folder contents
  const updatedTree = updateFolderInTree(assetTree, folderId, {
    folders: folderTree.folders,
    assets: folderTree.assets,
    isChildrenLoaded: true,
  });

  if (!updatedTree) {
    throw new Error('Failed to update folder in tree');
  }

  return updatedTree;
}

/**
 * Refresh folder contents
 * @param assetTree - Current asset tree
 * @param folderId - ID of folder to refresh
 * @param entityType - Entity type for API call
 * @param depth - Optional depth (defaults to 0 for direct children only)
 * @returns Updated tree with refreshed folder contents
 */
export async function refreshFolderUtil(
  assetTree: Folder | null,
  folderId: number,
  entityType: AssetFolderEntityType,
  depth = 0,
): Promise<Folder> {
  if (!assetTree) {
    throw new Error('Asset tree not loaded. Please load asset tree first.');
  }

  // Fetch folder contents
  const folderTree = await assetsApi.GetFolder(entityType, folderId, depth);

  // Update tree immutably with refreshed folder contents
  const updatedTree = updateFolderInTree(assetTree, folderId, {
    folders: folderTree.folders,
    assets: folderTree.assets,
    isChildrenLoaded: true,
  });

  if (!updatedTree) {
    throw new Error('Failed to update folder in tree');
  }

  return updatedTree;
}

/**
 * Create assets from files (handles S3 upload + job waiting)
 * @param entityType - Type of entity
 * @param folderId - ID of the folder to create assets in
 * @param files - Array of files to upload and create as assets
 * @returns Promise<number[]> - Array of created asset IDs
 */
export async function createAssetUtil(
  entityType: AssetFolderEntityType,
  folderId: number,
  files: File[],
): Promise<number[]> {
  if (files.length === 0) {
    return [];
  }

  const assetText = files.length === 1 ? 'asset' : 'assets';

  const normalizedFileNames: string[] = [];

  for (const file of files) {
    const result = normalizeAndValidateAssetFileName(file.name);

    if ('errorMessage' in result) {
      emit.error({message: result.errorMessage});

      return [];
    }

    normalizedFileNames.push(result.normalizedFileName);
  }

  return backgroundProcessService.startUIProcess({
    processName: `Uploading ${assetText}`,
    type: ProcessType.REMOTE,
    processFunc: async (bgProcess) => {
      let completedUploads = 0;

      const fileUrls = await Promise.all(
        files.map(async (file, index) => {
          const normalizedFileName = normalizedFileNames[index];
          const {presignedUrl, finalS3Url} = await fileUploadApi.getPresignedUrlForUpload(
            file.type,
            normalizedFileName,
          );

          bgProcess.setSubtitle(`Uploading: ${normalizedFileName}`);
          await fileUploadApi.uploadFileToS3({
            url: presignedUrl,
            file,
            contentType: file.type,
          });

          completedUploads++;
          bgProcess.progress((completedUploads / files.length) * 50);

          return finalS3Url;
        }),
      );

      const jobId = await assetsApi.createAssets(
        entityType,
        folderId,
        {
          fileUrls,
        },
        undefined,
      );

      bgProcess.setSubtitle(`Creating ${assetText}`);

      const jobResult = await waitForJob<number[] & JobResult>({
        jobId,
        bgProcess,
        subtitle: `Creating ${assetText}`,
      });

      return jobResult.result;
    },
  });
}

export interface FolderAssetUploadItem {
  file: File;
  assetPath: string;
}

/**
 * Create folders and assets from files (handles S3 upload + batch creation)
 * @param entityType - Type of entity
 * @param folderId - ID of the folder to create assets in
 * @param items - Array of files with their relative paths
 * @returns Promise<number> - Number of created assets
 */
export async function createFoldersAndAssetsUtil(
  entityType: AssetFolderEntityType,
  folderId: number,
  items: FolderAssetUploadItem[],
): Promise<number> {
  if (items.length === 0) {
    return 0;
  }

  const validationError = validateFileSizes(items);

  if (validationError) {
    emit.error({message: validationError});

    return Promise.resolve(0);
  }

  const assetText = items.length === 1 ? 'asset' : 'assets';

  const normalizedItems: {
    file: File;
    assetPath: string;
    normalizedFileName: string;
    normalizedAssetPath: string;
  }[] = [];

  for (const {file, assetPath} of items) {
    const result = normalizeAndValidateAssetFileName(file.name);

    if ('errorMessage' in result) {
      emit.error({message: result.errorMessage});

      return Promise.resolve(0);
    }

    const normalizedAssetPath = assetPath.replace(/\\/g, '/');
    const pathSegments = normalizedAssetPath.split('/');

    pathSegments[pathSegments.length - 1] = result.normalizedFileName;

    normalizedItems.push({
      file,
      assetPath,
      normalizedFileName: result.normalizedFileName,
      normalizedAssetPath: pathSegments.join('/'),
    });
  }

  // Start UI process for uploading assets and creating folders
  // Use the same bg Process for loading assets to s3 (UI process) and createFoldersAndAssets (API process)
  return backgroundProcessService.startUIProcess({
    processName: `Uploading ${assetText} and creating folders`,
    type: ProcessType.REMOTE,
    processFunc: async (bgProcess) => {
      // Validate file sizes before starting upload

      let completedUploads = 0;

      const assetRequestItems = await Promise.all(
        normalizedItems.map(async ({file, normalizedFileName, normalizedAssetPath}) => {
          const {presignedUrl, finalS3Url} = await fileUploadApi.getPresignedUrlForUpload(
            file.type,
            normalizedFileName,
          );

          await fileUploadApi.uploadFileToS3({
            url: presignedUrl,
            file,
            contentType: file.type,
          });

          // Update progress: 0-50% for uploads - only forward progress
          completedUploads++;
          bgProcess.progress((completedUploads / normalizedItems.length) * 50);
          bgProcess.setSubtitle(`Uploading: ${normalizedFileName}`);

          return {
            assetUrl: finalS3Url,
            assetPath: normalizedAssetPath,
          };
        }),
      );

      // API call - skip creating bgProcess since we use our own from startUIProcess
      const {jobId} = await assetsApi.createFoldersAndAssets(
        entityType,
        folderId,
        assetRequestItems,
        undefined,
        true, // skipBgProcess = true
      );

      bgProcess.setSubtitle(`Creating folder ${assetText}`);
      await waitForJob<JobResult>({
        jobId,
        bgProcess, // Pass the bgProcess from startUIProcess
        subtitle: `Creating folder ${assetText}`,
      });

      return assetRequestItems.length;
    },
  });
}
